import React, { useMemo, useState } from "react";

interface NewsArticleSummary {
  title: string;
  slug: string;
  url: string;
  snippet: string;
  author: string;
  published_at: string;
  tags?: string[];
}

interface NewsIndexProps {
  articles: NewsArticleSummary[];
}

function formatDate(dateString?: string) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 30) {
    return diffDays <= 0 ? "today" : `${diffDays}d ago`;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function authorAvatarUrl(author: string) {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=random`;
}

export default function NewsIndex({ articles }: NewsIndexProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((article) => {
      if (q.length === 0) return true;
      return (
        article.title?.toLowerCase().includes(q) ||
        article.snippet?.toLowerCase().includes(q) ||
        (article.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [articles, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <style>{styles}</style>

      <div className="news-page">
        <header className="news-hero">
          <p className="news-eyebrow">Decode Investing News</p>
          <h1 className="news-headline">
            Market News With a Value Investor's Verdict
          </h1>
          <p className="news-subhead">
            Cutting through the noise to expose opportunities and traps hiding in plain sight.
          </p>

          <div className="news-controls">
            <div className="news-search">
              <span className="news-search-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-3.6-3.6" />
                </svg>
              </span>
              <input
                type="search"
                placeholder="Search news"
                aria-label="Search news"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        </header>

        {filtered.length === 0 && (
          <div className="news-empty">
            <p>No articles match your search yet. Try a different keyword.</p>
          </div>
        )}

        {featured && (
          <a href={featured.url} className="news-featured" aria-label={featured.title}>
            <div className="news-featured-body">
              {(featured.tags || []).slice(0, 2).map((tag) => (
                <span key={tag} className="news-tag">{tag}</span>
              ))}
              <h2 className="news-featured-title">{featured.title}</h2>
              <p className="news-featured-snippet">{featured.snippet}</p>
              <div className="news-meta">
                <img
                  src={authorAvatarUrl(featured.author)}
                  alt={featured.author}
                  className="news-author-avatar"
                />
                <span className="news-author-name">{featured.author}</span>
                <span className="news-meta-dot">·</span>
                <time>{formatDate(featured.published_at)}</time>
              </div>
            </div>
          </a>
        )}

        {rest.length > 0 && (
          <div className="news-grid">
            {rest.map((article) => (
              <a key={article.slug} href={article.url} className="news-card" aria-label={article.title}>
                <div className="news-card-body">
                  <div className="news-card-tags">
                    {(article.tags || []).slice(0, 2).map((tag) => (
                      <span key={tag} className="news-tag">{tag}</span>
                    ))}
                  </div>
                  <h3 className="news-card-title">{article.title}</h3>
                  <p className="news-card-snippet">{article.snippet}</p>
                  <div className="news-meta">
                    <img
                      src={authorAvatarUrl(article.author)}
                      alt={article.author}
                      className="news-author-avatar"
                    />
                    <span className="news-author-name">{article.author}</span>
                    <span className="news-meta-dot">·</span>
                    <time>{formatDate(article.published_at)}</time>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const styles = `
  .news-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    max-width: 1120px;
    margin: 0 auto;
    padding: 32px 24px 80px;
    color: rgb(41, 41, 41);
  }

  .news-hero {
    padding: 16px 0 32px;
    border-bottom: 1px solid rgba(230, 230, 230, 0.8);
    margin-bottom: 40px;
  }

  .news-eyebrow {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgb(99, 102, 241);
    margin: 0 0 12px;
  }

  .news-headline {
    font-size: clamp(28px, 4.5vw, 44px);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: rgb(20, 20, 20);
    margin: 0 0 16px;
    max-width: 920px;
  }

  .news-subhead {
    font-size: clamp(16px, 2vw, 19px);
    line-height: 1.55;
    color: rgb(90, 90, 90);
    max-width: 760px;
    margin: 0 0 24px;
  }

  .news-controls {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
  }

  .news-search {
    position: relative;
    max-width: 520px;
  }

  .news-search-icon {
    position: absolute;
    top: 50%;
    left: 14px;
    transform: translateY(-50%);
    color: rgb(140, 140, 140);
    pointer-events: none;
  }

  .news-search input {
    width: 100%;
    padding: 12px 16px 12px 42px;
    font-size: 16px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: rgb(25, 25, 25);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .news-search input:focus {
    outline: none;
    border-color: rgb(99, 102, 241);
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
  }

  .news-empty {
    padding: 48px 0;
    text-align: center;
    color: rgb(120, 120, 120);
    font-size: 16px;
  }

  .news-featured {
    display: block;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.04), rgba(168, 85, 247, 0.04));
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 24px;
    padding: 28px 32px;
    margin-bottom: 56px;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .news-featured:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
    text-decoration: none;
    color: inherit;
  }

  .news-featured-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .news-featured-title {
    font-size: clamp(24px, 3vw, 34px);
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.018em;
    margin: 4px 0 0;
    color: rgb(20, 20, 20);
  }

  .news-featured-snippet {
    font-size: 17px;
    line-height: 1.65;
    color: rgb(80, 80, 80);
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .news-tag {
    display: inline-block;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-radius: 999px;
    background: rgba(99, 102, 241, 0.1);
    color: rgb(79, 70, 229);
    margin-right: 6px;
  }

  .news-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 32px;
  }

  .news-card {
    display: flex;
    flex-direction: column;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: #ffffff;
    text-decoration: none;
    color: inherit;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }

  .news-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.07);
    border-color: rgba(0, 0, 0, 0.12);
    text-decoration: none;
    color: inherit;
  }

  .news-card-body {
    padding: 20px 22px 22px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
  }

  .news-card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .news-card-title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.012em;
    color: rgb(20, 20, 20);
    margin: 0;
  }

  .news-card-snippet {
    font-size: 15px;
    line-height: 1.6;
    color: rgb(85, 85, 85);
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .news-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: rgb(120, 120, 120);
    margin-top: auto;
  }

  .news-author-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }

  .news-author-name {
    color: rgb(35, 35, 35);
    font-weight: 600;
    font-size: 13px;
  }

  .news-meta-dot {
    color: rgb(170, 170, 170);
  }

  @media (max-width: 640px) {
    .news-page {
      padding: 24px 16px 64px;
    }

    .news-grid {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .news-featured {
      padding: 16px;
    }

    .news-featured-title {
      font-size: 24px;
    }

    .news-card-title {
      font-size: 20px;
    }
  }

  .dark-mode .news-page {
    color: #d8d8d8;
  }

  .dark-mode .news-eyebrow {
    color: #b66dff;
  }

  .dark-mode .news-headline,
  .dark-mode .news-featured-title,
  .dark-mode .news-card-title,
  .dark-mode .news-author-name {
    color: #ffffff;
  }

  .dark-mode .news-subhead,
  .dark-mode .news-featured-snippet,
  .dark-mode .news-card-snippet {
    color: #b8bcc4;
  }

  .dark-mode .news-hero {
    border-bottom-color: #322f2f;
  }

  .dark-mode .news-search input {
    background: rgba(28, 26, 33, 0.85);
    border-color: rgba(255, 255, 255, 0.12);
    color: #f1f1f1;
  }

  .dark-mode .news-search input:focus {
    border-color: #b66dff;
    box-shadow: 0 0 0 4px rgba(182, 109, 255, 0.18);
  }

  .dark-mode .news-search-icon {
    color: rgba(255, 255, 255, 0.45);
  }

  .dark-mode .news-tag {
    background: rgba(182, 109, 255, 0.15);
    color: #d6b6ff;
  }

  .dark-mode .news-featured {
    background: linear-gradient(135deg, rgba(182, 109, 255, 0.06), rgba(99, 102, 241, 0.06));
    border-color: #322f2f;
  }

  .dark-mode .news-card {
    background: #1c1a21;
    border-color: #2a272f;
  }

  .dark-mode .news-card:hover {
    border-color: #3a3641;
    box-shadow: 0 14px 38px rgba(0, 0, 0, 0.45);
  }

  .dark-mode .news-meta-dot {
    color: #4a4853;
  }

  .dark-mode .news-meta {
    color: #9c9fa6;
  }
`;
